import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';
import jobStatuses from './constants/job-statuses';
import JobAssignmentFields from './components/JobAssignmentFields';

function AddJobModal({ customerId, customerName, siteId = null, siteName = null, onClose, onJobCreated }) {
  const [customer, setCustomer] = useState(null);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isHeadOffice, setIsHeadOffice] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState(siteId);
  const [lists, setLists] = useState({
    callTypes: [],
    callPriorities: [],
    responseTimes: [],
    jobCategories: [],
    doorTypes: []
  });
  const [zone, setZone] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [priceOverride, setPriceOverride] = useState('');
  const [workTypes, setWorkTypes] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [assignPersonId, setAssignPersonId] = useState('');
  const [assignDate, setAssignDate] = useState('');
  const [assignTime, setAssignTime] = useState('');

  const [formData, setFormData] = useState({
    job_status: jobStatuses.DEFAULT_JOB_STATUS,
    job_category: 'repair',
    call_type: '',
    call_priority: '',
    response_time: '',
    door_type: '',
    equipment_id: null,
    work_type: '',
    job_description: '',
    po_number: '',
    site_contact: '',
    site_number: '',
    opening_times: '',
    specific_requirements: ''
  });

  useEffect(() => {
    apiFetch('/api/employees')
      .then((res) => res.json())
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        const engineersOnly = list.filter((person) => {
          if (person.is_engineer === true || person.is_engineer === 't') return true;
          const role = `${person.role || ''} ${person.job_title || ''}`.toLowerCase();
          return role.includes('engineer') || role.includes('technician');
        });
        setEngineers(engineersOnly.length ? engineersOnly : list);
      })
      .catch((err) => {
        console.error('Error fetching engineers:', err);
        setEngineers([]);
      });
    fetchCustomerDetails();
    fetchLists();
    if (!siteId) {
      fetchCustomerSites();
    } else {
      fetchSiteDetails();
    }
  }, [customerId, siteId]);

  // Fetch work types when door_type changes
  useEffect(() => {
    if (formData.door_type) {
      fetchWorkTypes(formData.door_type);
    } else {
      setWorkTypes([]);
    }
  }, [formData.door_type]);

  const fetchWorkTypes = async (doorType) => {
    try {
      const res = await apiFetch(`/api/custom-lists?list_type=work_type&parent_value=${encodeURIComponent(doorType)}`);
      const data = await res.json();
      setWorkTypes(data);
    } catch (err) {
      console.error('Error fetching work types:', err);
      setWorkTypes([]);
    }
  };

  useEffect(() => {
    if (selectedSiteId && !siteId) {
      fetchSiteDetails();
    }
  }, [selectedSiteId]);

  // Fetch site equipment when a site is selected
  const [siteEquipment, setSiteEquipment] = useState([]);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);

  useEffect(() => {
    if (selectedSiteId || siteId) {
      const id = selectedSiteId || siteId;
      apiFetch(`/api/sites/${id}/equipment`).then(res => res.json()).then(data => {
        setSiteEquipment(Array.isArray(data) ? data.filter(e => e.is_active) : []);
      }).catch(err => {
        console.error('Error fetching site equipment:', err);
        setSiteEquipment([]);
      });
      setSelectedEquipmentId(null);
      setFormData((current) => ({ ...current, equipment_id: null }));
    } else {
      setSiteEquipment([]);
      setSelectedEquipmentId(null);
      setFormData((current) => ({ ...current, equipment_id: null }));
    }
  }, [selectedSiteId, siteId]);

  const handleEquipmentSelect = (equipId) => {
    const equip = siteEquipment.find(e => e.id === parseInt(equipId, 10));
    setSelectedEquipmentId(equip ? equip.id : null);
    setFormData((current) => ({
      ...current,
      equipment_id: equip ? equip.id : null,
      door_type: equip ? equip.equipment_type : current.door_type,
      work_type: equip ? '' : current.work_type,
    }));
  };

  // Auto-calculate price when site and response time are selected
  useEffect(() => {
    console.log('Pricing check:', {
      hasPostcode: !!selectedSite?.postcode,
      postcode: selectedSite?.postcode,
      hasResponseTime: !!formData.response_time,
      responseTime: formData.response_time
    });

    if (selectedSite?.postcode && formData.response_time) {
      console.log('Calculating price for:', selectedSite.postcode, formData.response_time);
      calculatePrice(selectedSite.postcode, formData.response_time);
    }
  }, [selectedSite, formData.response_time]);

  const fetchCustomerDetails = async () => {
    try {
      const res = await apiFetch(`/api/customers/${customerId}`);
      const data = await res.json();
      setCustomer(data);
    } catch (err) {
      console.error('Error fetching customer:', err);
    }
  };

  const fetchSiteDetails = async () => {
    try {
      const targetSiteId = siteId || selectedSiteId;
      const res = await apiFetch(`/api/sites/${targetSiteId}`);
      const data = await res.json();
      setSelectedSite(data);
    } catch (err) {
      console.error('Error fetching site:', err);
    }
  };

  const fetchCustomerSites = async () => {
    try {
      const res = await apiFetch('/api/sites');
      const allSites = await res.json();
      const customerSites = allSites.filter(site => site.customer_id === customerId);
      setSites(customerSites);
    } catch (err) {
      console.error('Error fetching sites:', err);
    }
  };

  const fetchLists = async () => {
    try {
      const res = await apiFetch('/api/lists');
      const data = await res.json();
      setLists({
        callTypes: data.callTypes || [],
        callPriorities: data.callPriorities || [],
        responseTimes: data.responseTimes || [],
        jobCategories: data.jobCategories || [],
        doorTypes: data.doorTypes || []
      });
    } catch (err) {
      console.error('Error fetching lists:', err);
    }
  };

  const calculatePrice = async (postcode, responseTime) => {
    try {
      const res = await apiFetch('/api/pricing/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcode, responseTime })
      });
      const data = await res.json();
      if (data.success) {
        setZone(data.zone);
        setCalculatedPrice(data.price);
        if (!priceOverride) {
          setPriceOverride(data.price.toFixed(2));
        }
      }
    } catch (err) {
      console.error('Error calculating price:', err);
      setZone(null);
      setCalculatedPrice(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const hasEngineer = String(assignPersonId || '').trim() !== '';
    const hasDate = String(assignDate || '').trim() !== '';
    if (hasEngineer !== hasDate) {
      alert('Choose both an engineer and a date, or leave both blank.');
      setLoading(false);
      return;
    }

    try {
      // Generate job number (simplified - backend should handle this)
      const jobNumber = `JOB-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`;

      const jobData = {
        job_number: jobNumber,
        customer_id: customerId,
        site_id: isHeadOffice ? null : selectedSiteId,
        zone: zone,
        quoted_price: parseFloat(priceOverride) || calculatedPrice,
        ...formData
      };
      if (hasEngineer && hasDate) {
        jobData.assign_person_id = Number(assignPersonId);
        jobData.assign_date = assignDate;
        jobData.assign_time = assignTime || '08:00';
      }

      const res = await apiFetch('/api/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(jobData)
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        onJobCreated(data);
        onClose();
      } else {
        alert(data.error || 'Failed to create job');
      }
    } catch (err) {
      console.error('Error creating job:', err);
      alert('Error creating job');
    }
    setLoading(false);
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Create New Job</h2>
              <p className="text-sm text-gray-600 mt-1">
                For: <span className="font-medium">{customerName}</span>
                {siteName && <span> → {siteName}</span>}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* Customer Details */}
          {customer && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <span className="text-blue-600 mr-2">👤</span> Customer Details
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-600">Company:</span>
                  <span className="ml-2 font-medium">{customer.company_name}</span>
                </div>
                <div>
                  <span className="text-gray-600">Contact:</span>
                  <span className="ml-2 font-medium">{customer.contact_name || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Phone:</span>
                  <span className="ml-2 font-medium">{customer.phone || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Mobile:</span>
                  <span className="ml-2 font-medium">{customer.mobile || 'N/A'}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">Email:</span>
                  <span className="ml-2 font-medium">{customer.email || 'N/A'}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">Address:</span>
                  <span className="ml-2 font-medium">
                    {[customer.address, customer.city, customer.county, customer.postcode]
                      .filter(Boolean)
                      .join(', ') || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Site Selection (only if not from site page) */}
          {!siteId && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <label className="flex items-center space-x-2 mb-3">
                <input
                  type="checkbox"
                  checked={isHeadOffice}
                  onChange={(e) => {
                    setIsHeadOffice(e.target.checked);
                    if (e.target.checked) {
                      setSelectedSiteId(null);
                    }
                  }}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="font-medium text-gray-900">
                  🏢 This is a Head Office job
                </span>
              </label>

              {!isHeadOffice && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Site *
                  </label>
                  <select
                    value={selectedSiteId || ''}
                    onChange={(e) => setSelectedSiteId(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required={!isHeadOffice}
                  >
                    <option value="">Choose a site...</option>
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>
                        {site.site_name} - {site.city || site.postcode}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Site Details */}
          {(isHeadOffice || selectedSite) && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <span className="text-green-600 mr-2">📍</span>
                {isHeadOffice ? 'Head Office' : 'Site Details'}
              </h3>
              {isHeadOffice && customer ? (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="col-span-2">
                    <span className="text-gray-600">Location:</span>
                    <span className="ml-2 font-medium">
                      {[customer.address, customer.city, customer.county, customer.postcode]
                        .filter(Boolean)
                        .join(', ') || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Contact:</span>
                    <span className="ml-2 font-medium">{customer.contact_name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Phone:</span>
                    <span className="ml-2 font-medium">{customer.phone || customer.mobile || 'N/A'}</span>
                  </div>
                </div>
              ) : selectedSite ? (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="col-span-2">
                    <span className="text-gray-600">Site Name:</span>
                    <span className="ml-2 font-medium">{selectedSite.site_name}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Address:</span>
                    <span className="ml-2 font-medium">
                      {[selectedSite.address, selectedSite.city, selectedSite.county, selectedSite.postcode]
                        .filter(Boolean)
                        .join(', ') || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Site Contact:</span>
                    <span className="ml-2 font-medium">{selectedSite.site_contact || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Phone:</span>
                    <span className="ml-2 font-medium">{selectedSite.site_number || 'N/A'}</span>
                  </div>
                  {selectedSite.opening_times && (
                    <div className="col-span-2">
                      <span className="text-gray-600">Opening Times:</span>
                      <span className="ml-2 font-medium">{selectedSite.opening_times}</span>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}

          <JobAssignmentFields
            personId={assignPersonId}
            date={assignDate}
            time={assignTime}
            engineers={engineers}
            onPerson={(value) => {
              setAssignPersonId(value);
              if (value && !assignTime) setAssignTime('08:00');
            }}
            onDate={setAssignDate}
            onTime={setAssignTime}
          />

          {/* Job Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Status
              </label>
              <select
                value={formData.job_status}
                onChange={(e) => handleChange('job_status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {jobStatuses.JOB_STATUSES.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Category
              </label>
              <select
                value={formData.job_category}
                onChange={(e) => handleChange('job_category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select category...</option>
                {lists.jobCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Call Type, Priority, Response Time */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📞 Call Type *
              </label>
              <select
                value={formData.call_type}
                onChange={(e) => handleChange('call_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              >
                <option value="">Select type...</option>
                {lists.callTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🔥 Call Priority *
              </label>
              <select
                value={formData.call_priority}
                onChange={(e) => handleChange('call_priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              >
                <option value="">Select priority...</option>
                {lists.callPriorities.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ⏱️ Response Time *
              </label>
              <select
                value={formData.response_time}
                onChange={(e) => handleChange('response_time', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              >
                <option value="">Select response time...</option>
                {lists.responseTimes.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Equipment (from site record) + Work Type */}
          {siteEquipment.length > 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm flex items-center">
                <span className="text-green-600 mr-2">🔧</span> Site Equipment
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Equipment *
                  </label>
                  <select
                    value={selectedEquipmentId || ''}
                    onChange={(e) => handleEquipmentSelect(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Choose equipment...</option>
                    {siteEquipment.map(equip => (
                      <option key={equip.id} value={equip.id}>
                        {equip.manufacturer} {equip.model} — {equip.location_description || equip.equipment_type}
                      </option>
                    ))}
                  </select>
                  {selectedEquipmentId ? (
                    <p className="text-sm text-gray-600 mt-2">
                      Door / gate location: {siteEquipment.find((item) => item.id === selectedEquipmentId)?.location_description || 'Not stored'}
                    </p>
                  ) : null}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Equipment Type (auto)
                    </label>
                    <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-600">
                      {formData.door_type || '—'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🔧 Work Type
                    </label>
                    <select
                      value={formData.work_type}
                      onChange={(e) => handleChange('work_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      disabled={!formData.door_type}
                    >
                      <option value="">Select work type...</option>
                      {workTypes.map(wt => (
                        <option key={wt.id} value={wt.list_value}>{wt.list_value}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm flex items-center">
                <span className="text-blue-600 mr-2">🔧</span> Equipment Type (New Callout)
              </h3>
              <p className="text-xs text-gray-500 mb-3">No equipment registered for this site. Select equipment type manually — a standard RAMS will be generated.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Equipment Type *
                  </label>
                  <select
                    value={formData.door_type}
                    onChange={(e) => {
                      handleChange('door_type', e.target.value);
                      handleChange('work_type', '');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Select equipment type...</option>
                    {lists.doorTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Work Type
                  </label>
                  <select
                    value={formData.work_type}
                    onChange={(e) => handleChange('work_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    disabled={!formData.door_type}
                  >
                    <option value="">Select work type...</option>
                    {workTypes.map(wt => (
                      <option key={wt.id} value={wt.list_value}>{wt.list_value}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Quoted Price */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                💰 Quoted Price {calculatedPrice && <span className="text-xs text-gray-500">(Auto: £{calculatedPrice.toFixed(2)})</span>}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">£</span>
                <input
                  type="number"
                  step="0.01"
                  value={priceOverride}
                  onChange={(e) => setPriceOverride(e.target.value)}
                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Description *
            </label>
            <textarea
              value={formData.job_description}
              onChange={(e) => handleChange('job_description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              placeholder="Describe the work to be done..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              PO Number
            </label>
            <input
              type="text"
              value={formData.po_number}
              onChange={(e) => handleChange('po_number', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., PO-12345"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site Contact
              </label>
              <input
                type="text"
                value={formData.site_contact}
                onChange={(e) => handleChange('site_contact', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Contact name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site Phone Number
              </label>
              <input
                type="text"
                value={formData.site_number}
                onChange={(e) => handleChange('site_number', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Phone number"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Opening Times
            </label>
            <input
              type="text"
              value={formData.opening_times}
              onChange={(e) => handleChange('opening_times', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Mon-Fri 9am-5pm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Specific Requirements
            </label>
            <textarea
              value={formData.specific_requirements}
              onChange={(e) => handleChange('specific_requirements', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="2"
              placeholder="Any special requirements or notes..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (!isHeadOffice && !selectedSiteId && !siteId)}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '⏳ Creating...' : '✓ Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddJobModal;
